import {
  Box,
  Button,
  Heading,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";

import { Layout, Header, Content } from "#app/components/layouts/AppPage.jsx";
import { Paper } from "#app/components/Paper.jsx";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";

/**
 * /alberta-parks — the canonical ongoing-comms page for the Aspira-cohort
 * shutdown (Alberta, Saskatchewan, PEI). This is an evergreen editorial page,
 * NOT a blog post: it is the target of the facility-search unavailable state,
 * the /new/$facility direct-URL redirect, the old blog post's canonical tag,
 * and the support inbox macro.
 *
 * Query params (all optional, page renders correctly without any):
 *   - from:   "facility" | "search" | undefined
 *   - slug:   original facility slug that redirected here (from=facility)
 *   - q:      original search query string (from=search)
 *   - agency: "AlbertaParks" | "SKParks" | "PEIParks"
 *
 * Copy is founder-authored and lives inline. Section structure matches the
 * 8-section arc in the Alberta Parks ongoing-comms design doc:
 *
 *   1. TL;DR box (above the fold)
 *   2. Your options (two equal-weight cards)
 *   3. How to still get Alberta Parks camping (Spot Watch how-to)
 *   4. What happened
 *   5. What I tried
 *   6. Where things stand
 *   7. Share your story (optional)
 *   8. SK Parks and PEI Parks footnote
 *
 * July 2026: Alberta Parks launched "Spot Watch", their own free
 * availability-alert system (multi-park watches, up to 30 arrival dates,
 * email/SMS, pause/edit/remove). Section 3 documents it; the old
 * search-first "Create Availability Notification" walkthrough is gone.
 * Official how-to: https://shop.albertaparks.ca/resource/contracts/abpp/Spot_Watch.pdf
 */

const LAST_UPDATED = "July 2026";

export function meta({ matches }) {
  const baseUrl = getBaseUrl(matches);
  return getSeoMeta({
    title: "Alberta Parks on Schnerp",
    description:
      "The current status of Alberta Parks on Schnerp, the story behind it, and how to use Alberta Parks' free Spot Watch alerts.",
    url: `${baseUrl}/alberta-parks`,
    image: getDefaultOgImage(baseUrl),
  });
}

export default function AlbertaParksPage() {
  const tldrBorder = useColorModeValue("brand.500", "brand.200");
  const imgBorder = useColorModeValue("gray.200", "gray.700");

  return (
    <Layout maxW="3xl">
      <Header
        title="Alberta Parks on Schnerp"
        subtitle={`Last updated ${LAST_UPDATED}`}
      />
      <Content>
        {/* 1. TL;DR — lead with the fact, then the honest framing */}
        <Paper borderLeftWidth="4px" borderLeftColor={tldrBorder} my={6}>
          <Text fontSize={{ base: "md", md: "lg" }}>
            Alberta Parks has been off Schnerp since August 2025, when they
            asked me to stop scanning their reservation system. I&rsquo;ve tried
            every path back I could find, and none of them worked. They&rsquo;ve
            since launched Spot Watch, a free notification system of their own
            that covers most of what Schnerp did here. This page is the honest
            version of what happened, and what you can do next.
          </Text>
        </Paper>

        {/* 2. Your options — pulled directly below the TL;DR so a
             frustrated camper gets the answer in the first scroll.
             Full container width. */}
        <Box as="section" mt={8}>
          <Heading
            as="h2"
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="bold"
            letterSpacing="tight"
            mb={4}
          >
            Your options
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 5 }}>
            <OptionCard
              title="Keep using Schnerp for other parks"
              body="Most other parks systems are still running — including national parks and private operators within Alberta, Saskatchewan, and PEI themselves. New Zealand, Australia, and a handful of US parks are all on Schnerp too. If your next trip is anywhere outside those three provincial systems, Schnerp works the way it always has."
              cta={{
                label: "Browse other parks",
                variant: "outline",
                href: "/new",
                ariaLabel: "Browse other parks on Schnerp",
              }}
            />
            <OptionCard
              title="Alberta Parks was my only reason"
              body="I did a round of refunds back in September 2025 for anyone only using Schnerp for Alberta Parks. If you missed it, or things have changed, email me and I'll sort it out."
              cta={{
                label: "Email me",
                variant: "primary",
                href: "mailto:help@schnerp.com?subject=Alberta%20Parks%20refund",
                ariaLabel:
                  "Email help@schnerp.com to request an Alberta Parks refund",
              }}
            />
          </SimpleGrid>
        </Box>

        {/* 3. Prose stack — action first (how to still get camping),
             then the full story for anyone who keeps reading. */}
        <Stack spacing={8} maxW={{ base: "100%", md: "65ch" }} mt={10}>
          {/* Action: honest endorsement of Spot Watch, Alberta Parks'
               own notification system. Trust is built or lost here. */}
          <Section heading="How to still camp in Alberta Parks">
            <Stack spacing={4}>
              <Text>
                Alberta Parks now runs <b>Spot Watch</b>, a free availability
                alert system built into{" "}
                <Link
                  href="https://shop.albertaparks.ca"
                  isExternal
                  color="accent"
                  textDecoration="underline"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  shop.albertaparks.ca
                </Link>
                . Look for the Spot Watch button in the top menu.
              </Text>
              <Text>
                It covers most of what Schnerp did here. One watch can follow
                one park, several, or all of them, narrowed to specific
                campgrounds, loops, or sites. Pick up to 30 arrival dates, set
                your nights, and get alerts by email, text, or both.
                Here&rsquo;s the{" "}
                <Link
                  href="https://shop.albertaparks.ca/resource/contracts/abpp/Spot_Watch.pdf"
                  isExternal
                  color="accent"
                  textDecoration="underline"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  step-by-step guide
                </Link>{" "}
                if you want it.
              </Text>
              <Box as="figure" my={2}>
                <Box
                  as="img"
                  src="/alberta-parks/spot-watch-builder.png"
                  alt="Alberta Parks Spot Watch form. Under 'Select your arrival dates', an August 2026 calendar has three Fridays selected, listed as chips beside it, with 'You have selected 3 date(s) to watch.' Below, a nights/days stepper is set to 1."
                  w="100%"
                  rounded="md"
                  borderWidth="1px"
                  borderColor={imgBorder}
                />
                <Text as="figcaption" fontSize="xs" color="muted" mt={2}>
                  The Spot Watch form: pick your arrival dates and how many
                  nights you want to stay.
                </Text>
              </Box>
              <Text>
                If you&rsquo;ve used Schnerp before, Spot Watch will feel
                familiar, and that makes me proud. Schnerp spent four years
                learning what campers need to catch a cancellation, and now the
                best of it is built directly into the booking site, free, for
                everyone.
              </Text>
            </Stack>
          </Section>

          {/* Story begins here. The rest of the page is the full
               record for anyone who wants it. */}
          <Section heading="What happened">
            <Stack spacing={4}>
              <Text>
                In August 2025, the vendor that runs Alberta Parks&rsquo;
                reservation platform wrote to me asking me to stop scanning. I
                wrote directly to Alberta Parks to confirm where the request had
                come from, and paused scanning while I waited to hear back.
              </Text>
              <Text>
                Three days later, they confirmed the decision was theirs. The
                reason they gave was that Schnerp&rsquo;s scanning had been
                affecting user experience on shop.albertaparks.ca, and they
                pointed me to the free notification system built into their
                reservation site as the alternative for campers.
              </Text>
            </Stack>
          </Section>

          {/* What I tried — personal story: Schnerp-user lawyer,
               Schnerp-user connection to meeting, three reasons not
               one. The meeting happened but the door was already
               closed going in. */}
          <Section heading="What I tried">
            <Stack spacing={4}>
              <Text>
                Alberta Parks&rsquo; Terms of Use describe a process for making
                a formal request. I&rsquo;d never hired a lawyer before, but a
                Schnerp user who happens to work at Blakes, one of
                Canada&rsquo;s largest law firms, offered to walk that path on
                my behalf. Formally, in writing, with data.
              </Text>
              <Text>
                I asked four separate times by email for a meeting, in person or
                over video, to talk through their concerns and find a version of
                Schnerp they&rsquo;d be comfortable with. Those emails went
                unanswered. Eventually another Schnerp user opened a side door
                for me through a different part of government, and I got the
                call.
              </Text>
              <Text>
                Two senior officials gave me their time on a video call, and
                I&rsquo;m genuinely grateful they did. I&rsquo;ll also be
                honest: the door was already closed going in. This wasn&rsquo;t
                a negotiation. It was a chance for me to hear them out, and for
                them to explain why the answer was no.
              </Text>
              <Text>
                They gave me three reasons, not one. Here&rsquo;s each one, and
                what I think about it.
              </Text>
              <Text>
                The first was technical. Alberta Parks had moved to a new
                reservation vendor not long before, and they said the team was
                sensitive about load during that transition. My incentives are
                aligned with theirs &mdash; Schnerp depends on the reservation
                site working &mdash; so I offered to work together on whatever
                rate limits they&rsquo;d be comfortable with. Without specifics
                or a conversation with their technical team, there was no way to
                align on what that actually looked like. That offer wasn&rsquo;t
                taken up.
              </Text>
              <Text>
                The second was product. Alberta Parks already runs a free
                notification system of their own, and in an ideal world
                that&rsquo;d be all anyone needed. But the gaps are real, and
                Schnerp users tell me about them constantly: no text messages,
                no backcountry permits, and flexible date ranges that take
                dozens of separate notifications to approximate. Both things can
                be true. Their system works for a certain kind of trip. Schnerp
                worked for the rest. (Spot Watch, which came later, closes most
                of those gaps. I&rsquo;ve written about it above.)
              </Text>
              <Text>
                The third was fairness. A paid service layered on top of a
                publicly-funded camping fee, they argued, gives an advantage to
                campers who can afford the subscription over those who
                can&rsquo;t. The vast majority of people use Schnerp for free.
                Paid users bought a better experience, like text messages and
                flexible date ranges. But on a booking system where spots
                disappear in seconds, better is faster. That&rsquo;s the part of
                the concern I don&rsquo;t have a clean answer to. Of the three
                reasons, it&rsquo;s the one I&rsquo;m still wrestling with.
              </Text>
              <Text>
                I made my case on all three. We had the conversation. The
                decision still stood.
              </Text>
            </Stack>
          </Section>

          {/* Where things stand — close the Alberta door honestly, then
               segue into the broader government conversation this opened
               up. No names, no agencies, no overpromise. */}
          <Section heading="Where things stand">
            <Stack spacing={4}>
              <Text>
                The request has been declined, and that decision stands today.
                I&rsquo;m not going to promise you whether or when that will
                change.
              </Text>
              <Text>
                One thing worth adding, though. The past year has also opened a
                different kind of conversation. Quieter ones, with people in
                government, about how private tools and public parks systems
                could work together more directly. Some of them are listening.
                That&rsquo;s a long play, not a short one. It doesn&rsquo;t
                bring Schnerp back to Alberta Parks, and it doesn&rsquo;t help
                anyone reading this page find a campsite next weekend. But
                it&rsquo;s the part of this story I&rsquo;m most excited about,
                and I didn&rsquo;t want this page to be only about the door that
                closed.
              </Text>
            </Stack>
          </Section>

          {/* Share your story — optional, framed as private
               record-keeping. Do NOT mention government, partners,
               policy, or ongoing conversations. */}
          <Section heading="Share your story">
            <Stack spacing={4}>
              <Text>
                I keep a private record of the trips Schnerp has helped make
                happen. Partly because it&rsquo;s why I started this. Partly
                because on the hard days, I need the reminder. If Schnerp ever
                helped you land a spot you were watching for, I&rsquo;d love to
                hear about it.
              </Text>
              <Box>
                <Button
                  as={Link}
                  href="mailto:daniel@schnerp.com?subject=My%20Schnerp%20story"
                  variant="primary"
                  size="sm"
                  aria-label="Email daniel@schnerp.com to share your Schnerp story"
                  _hover={{ textDecoration: "none" }}
                >
                  Tell me about it
                </Button>
              </Box>
              <Text fontSize="sm" color="muted">
                Or reach me at{" "}
                <Link
                  href="mailto:daniel@schnerp.com"
                  color="accent"
                  textDecoration="underline"
                >
                  daniel@schnerp.com
                </Link>
                .
              </Text>
            </Stack>
          </Section>
        </Stack>

        {/* 8. SK Parks and PEI Parks footnote — small, full-width, before
             the signature. One paragraph. */}
        <Text fontSize="sm" color="muted" mt={10}>
          A note on Saskatchewan Provincial Parks and PEI Provincial Parks: the
          same vendor runs all three reservation platforms, and the situation
          extended to both. Schnerp monitored them in previous years and stopped
          in 2025. If you came here looking for Saskatchewan or PEI, the same
          story applies, and the same options are open to you. Email me and
          I&rsquo;ll make it right.
        </Text>

        {/* Signature — matches the sign-off on the Sep 2025 blog post */}
        <Text
          mt={10}
          color="fg-subtle"
          fontSize="sm"
          textAlign={{ base: "left", md: "right" }}
        >
          &mdash;Daniel
        </Text>

        {/* Boilerplate independence disclaimer */}
        <Text mt={12} fontSize="xs" color="muted">
          Schnerp is an independent service. It is not affiliated with or
          endorsed by Alberta Parks or any other park agency.
        </Text>
      </Content>
    </Layout>
  );
}

function Section({ heading, children }) {
  return (
    <Box as="section">
      <Heading
        as="h2"
        fontSize={{ base: "lg", md: "xl" }}
        mb={3}
        fontWeight="bold"
        letterSpacing="tight"
      >
        {heading}
      </Heading>
      {children}
    </Box>
  );
}

function OptionCard({ title, body, cta }) {
  return (
    <Paper p={{ base: 4, md: 5 }}>
      <VStack align="stretch" spacing={3} h="100%">
        <Heading
          as="h3"
          fontSize="md"
          fontWeight="bold"
          letterSpacing="tight"
          lineHeight="1.25"
        >
          {title}
        </Heading>
        <Text fontSize="sm" flex="1">
          {body}
        </Text>
        <Button
          as={Link}
          href={cta.href}
          variant={cta.variant}
          size="sm"
          aria-label={cta.ariaLabel}
          whiteSpace="normal"
          _hover={{ textDecoration: "none" }}
        >
          {cta.label}
        </Button>
      </VStack>
    </Paper>
  );
}
